import { useState } from "react";
import { Header } from "@/components/Header";
import { useAuth } from "@/contexts/AuthContext";
import {
  BookOpen, Target, Clock, TrendingUp, ChevronRight,
  AlertTriangle, CheckCircle2, Flame, Search, ArrowRight,
} from "lucide-react";
import { useSubjects } from "@/contexts/SubjectsContext";
import type { Subject } from "@/data/subjects";

export function DashboardPage() {
  const { user, stats, loading } = useAuth();
  const { subjects } = useSubjects();
  const [subjectSearch, setSubjectSearch] = useState("");

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fc] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#1a3a6b] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#5a6a7e] text-sm">Loading your dashboard…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    window.location.href = "/login";
    return null;
  }

  const s = stats || {
    totalAttempted: 0, totalCorrect: 0, accuracy: 0, totalTimeSpent: 0,
    weakTopics: [], lastSubjectId: null, lastModuleId: null, lastTopicId: null,
    statsBySubject: {}, statsByTopic: {},
  };

  const formatTime = (sec: number) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const firstName = user.name?.split(" ")[0] || "Student";

  const filteredSubjects = subjects.filter(sub =>
    sub.title.toLowerCase().includes(subjectSearch.toLowerCase()) ||
    sub.description.toLowerCase().includes(subjectSearch.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#f8f9fc] flex flex-col">
      <Header />

      {/* ── Hero banner ── */}
      <div className="bg-gradient-to-r from-[#070f24] via-[#0d2248] to-[#1a3a6b]">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="text-[#f5a623] text-sm font-semibold tracking-wide uppercase mb-2">Your Dashboard</p>
              <h1 className="text-2xl md:text-3xl font-extrabold text-white mb-1">
                Welcome back, {firstName}! 👋
              </h1>
              <p className="text-white/60 text-sm">Here's an overview of your learning progress.</p>
            </div>
            {/* Quick stats in banner */}
            <div className="flex gap-6">
              <div className="text-center">
                <div className="text-3xl font-black text-[#f5a623]">{s.totalAttempted}</div>
                <div className="text-white/60 text-xs mt-0.5">Questions</div>
              </div>
              <div className="w-px bg-white/10" />
              <div className="text-center">
                <div className="text-3xl font-black text-[#f5a623]">{s.accuracy.toFixed(0)}%</div>
                <div className="text-white/60 text-xs mt-0.5">Accuracy</div>
              </div>
              <div className="w-px bg-white/10" />
              <div className="text-center">
                <div className="text-3xl font-black text-[#f5a623]">{formatTime(s.totalTimeSpent) || "0m"}</div>
                <div className="text-white/60 text-xs mt-0.5">Practiced</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-12">

        {/* ── Stat cards ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {[
            { label: "Questions Attempted", value: s.totalAttempted, icon: Target,       color: "bg-blue-50 text-blue-600" },
            { label: "Correct Answers",     value: s.totalCorrect,   icon: CheckCircle2, color: "bg-green-50 text-green-600" },
            { label: "Overall Accuracy",    value: `${s.accuracy.toFixed(1)}%`, icon: TrendingUp, color: "bg-purple-50 text-purple-600" },
            { label: "Time Practiced",      value: formatTime(s.totalTimeSpent) || "0m", icon: Clock, color: "bg-amber-50 text-amber-600" },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="bg-white border border-[#dde3ed] rounded-2xl p-5 shadow-sm">
              <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center mb-3`}>
                <Icon className="w-5 h-5" />
              </div>
              <p className="text-2xl font-extrabold text-[#1a3a6b] leading-none mb-1">{value}</p>
              <p className="text-xs text-[#5a6a7e] font-medium">{label}</p>
            </div>
          ))}
        </div>

        {/* ── Two-column cards ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-14">

          {/* Continue Practicing */}
          <div className="bg-white border border-[#dde3ed] rounded-2xl shadow-sm overflow-hidden">
            <div className="h-1.5 bg-[#f5a623]" />
            <div className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-[#1a3a6b]/10 flex items-center justify-center">
                  <Flame className="w-5 h-5 text-[#1a3a6b]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#1a2435]">Continue Practicing</h3>
                  <p className="text-xs text-[#5a6a7e]">Pick up where you left off</p>
                </div>
              </div>

              {s.lastSubjectId ? (
                <div>
                  <p className="text-sm text-[#5a6a7e] mb-4">
                    Last practiced: <span className="text-[#1a2435] font-semibold">{(s.lastTopicId || s.lastSubjectId || '').replace(/_/g, ' ')}</span>
                  </p>
                  <button
                    onClick={() => {
                      const url = s.lastModuleId
                        ? `/practice?subject=${s.lastSubjectId}&module=${s.lastModuleId}`
                        : `/subjects/${s.lastSubjectId}`;
                      window.location.href = url;
                    }}
                    className="w-full flex items-center justify-center gap-2 bg-[#1a3a6b] hover:bg-[#12265a] text-white font-semibold py-2.5 px-4 rounded-xl transition-colors text-sm"
                  >
                    Resume Practice <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-[#5a6a7e] mb-4">You haven't started yet. Let's begin!</p>
                  <button
                    onClick={() => (window.location.href = "/subjects")}
                    className="w-full flex items-center justify-center gap-2 bg-[#f5a623] hover:bg-[#e09310] text-[#12265a] font-bold py-2.5 px-4 rounded-xl transition-colors text-sm"
                  >
                    Start Practicing <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Focus Areas */}
          <div className="bg-white border border-[#dde3ed] rounded-2xl shadow-sm overflow-hidden">
            <div className="h-1.5 bg-orange-400" />
            <div className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <h3 className="font-bold text-[#1a2435]">Focus Areas</h3>
                  <p className="text-xs text-[#5a6a7e]">Topics that need more practice</p>
                </div>
              </div>

              {s.weakTopics.length > 0 ? (
                <ul className="space-y-1">
                  {s.weakTopics.slice(0, 5).map((topicId) => (
                    <li key={topicId} className="flex items-center justify-between py-2 border-b border-[#dde3ed] last:border-0">
                      <span className="text-sm text-[#1a2435]">{topicId}</span>
                      <span className="text-xs font-semibold text-orange-500 bg-orange-50 px-2 py-0.5 rounded-full">
                        {s.statsByTopic[topicId]?.accuracy.toFixed(0) || 0}%
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-[#5a6a7e]">Complete more questions to see your focus areas.</p>
              )}
            </div>
          </div>
        </div>

        {/* ── Question Banks ── */}
        <div>
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-xl font-bold text-[#1a2435]">Modules</h2>
              <p className="text-sm text-[#5a6a7e]">Select a subject to start practicing</p>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a6a7e] pointer-events-none" />
              <input
                type="text"
                placeholder="Search subjects…"
                value={subjectSearch}
                onChange={(e) => setSubjectSearch(e.target.value)}
                className="pl-9 pr-4 py-2 text-sm rounded-xl border border-[#dde3ed] bg-white focus:outline-none focus:ring-2 focus:ring-[#1a3a6b]/20 focus:border-[#1a3a6b] transition-colors w-52 placeholder:text-[#5a6a7e]"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSubjects.map((subject: Subject) => {
              const totalQ = subject.modules.reduce(
                (acc: number, m) => acc + m.topics.reduce((a: number, t) => a + (t.questionCount || 0), 0), 0
              );
              const isComingSoon = subject.comingSoon;
              const cardClass = isComingSoon
                ? "bg-white border border-[#dde3ed] rounded-2xl overflow-hidden shadow-sm opacity-70 cursor-default"
                : "group bg-white border border-[#dde3ed] rounded-2xl overflow-hidden shadow-sm hover:shadow-md hover:border-[#1a3a6b]/30 transition-all duration-200";

              const inner = (
                <>
                  <div className={`h-2 bg-gradient-to-r ${subject.gradient}`} />
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className={`font-bold text-base leading-tight ${isComingSoon ? "text-[#5a6a7e]" : "text-[#1a2435] group-hover:text-[#1a3a6b] transition-colors"}`}>
                        {subject.title}
                      </h3>
                      {isComingSoon ? (
                        <span className="text-xs font-bold text-amber-700 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded-full whitespace-nowrap ml-2">
                          Coming Soon
                        </span>
                      ) : totalQ > 0 ? (
                        <span className="text-xs font-semibold text-[#1a3a6b] bg-[#1a3a6b]/10 px-2 py-0.5 rounded-full whitespace-nowrap ml-2">
                          {totalQ.toLocaleString()}+ Qs
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs text-[#5a6a7e] mb-4 line-clamp-2 leading-relaxed">
                      {isComingSoon ? "Question bank coming soon. Tutoring available now." : subject.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-[#5a6a7e]">
                        {subject.modules.length} module{subject.modules.length !== 1 ? "s" : ""}
                      </span>
                      {isComingSoon ? (
                        <span className="text-xs font-semibold text-[#5a6a7e]">Coming soon</span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs font-semibold text-[#f5a623] group-hover:translate-x-0.5 transition-transform">
                          Practice <ChevronRight className="w-3.5 h-3.5" />
                        </span>
                      )}
                    </div>
                  </div>
                </>
              );

              return isComingSoon ? (
                <div key={subject.id} className={cardClass}>{inner}</div>
              ) : (
                <a key={subject.id} href={`/subjects/${subject.id}`} className={cardClass}>{inner}</a>
              );
            })}
          </div>
        </div>
      </main>

      {/* Simple footer */}
      <footer className="border-t border-[#dde3ed] bg-white mt-8 py-6">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#5a6a7e]">
          <div className="flex items-center gap-2">
            <img src="/logo.svg" alt="Scholar Sphere" className="h-6" />
            <span>© {new Date().getFullYear()} Scholar Sphere</span>
          </div>
          <div className="flex gap-5">
            <a href="/" className="hover:text-[#1a3a6b]">Home</a>
            <a href="/subjects" className="hover:text-[#1a3a6b]">Question Banks</a>
            <a href="/privacy" className="hover:text-[#1a3a6b]">Privacy</a>
            <a href="/terms" className="hover:text-[#1a3a6b]">Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
